import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { mostrarToast } from '@/lib/formularioGuiado';
import Cartao from '@/components/ui/Cartao';
import Botao from '@/components/ui/Botao';
import Chip from '@/components/ui/Chip';
import Modal from '@/components/ui/Modal';
import CampoArea from '@/components/base/CampoArea';
import type { PedidoExclusao } from '../types';
import { formatarDataBR } from '../utils';

function normalizar(data: unknown): PedidoExclusao[] {
  const lista = Array.isArray(data)
    ? data
    : data && typeof data === 'object' && Array.isArray((data as { pedidos?: unknown }).pedidos)
      ? ((data as { pedidos: unknown[] }).pedidos as unknown[])
      : [];

  return lista
    .filter((p): p is Record<string, unknown> => !!p && typeof p === 'object')
    .map((p) => ({
      id: Number(p.id),
      nome: (p.nome as string | null) ?? null,
      email: (p.email as string | null) ?? null,
      motivo: (p.motivo as string | null) ?? null,
      situacao: typeof p.situacao === 'string' ? p.situacao : '',
      pedido_em: (p.pedido_em as string | null) ?? null,
      dias: Number(p.dias) || 0,
      concluido_em: (p.concluido_em as string | null) ?? null,
      observacao: (p.observacao as string | null) ?? null,
    }));
}

function estaConcluido(p: PedidoExclusao): boolean {
  return p.situacao === 'concluido' || !!p.concluido_em;
}

export default function CartaoExclusoes({ aoAtualizar }: { aoAtualizar: () => void }) {
  const [pedidos, setPedidos] = useState<PedidoExclusao[] | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [alvo, setAlvo] = useState<PedidoExclusao | null>(null);
  const [observacao, setObservacao] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erroModal, setErroModal] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const { data, error } = await supabase.rpc('plataforma_exclusoes');
      if (error) throw error;
      setPedidos(normalizar(data));
    } catch (e) {
      setErro(mensagemAmigavel(e));
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  function abrir(p: PedidoExclusao) {
    setAlvo(p);
    setObservacao('');
    setErroModal(null);
  }

  async function concluir() {
    if (!alvo) return;
    setEnviando(true);
    setErroModal(null);
    try {
      const { data, error } = await supabase.rpc('plataforma_exclusao_concluir', {
        p_id: alvo.id,
        p_observacao: observacao.trim() || null,
      });
      if (error) throw error;
      const d = (data ?? {}) as { recado?: string | null };
      setAlvo(null);
      mostrarToast(d.recado ?? 'Pedido marcado como concluído.', 4000, 'sucesso');
      await carregar();
      aoAtualizar();
    } catch (e) {
      setErroModal(mensagemAmigavel(e));
    } finally {
      setEnviando(false);
    }
  }

  const abertos = (pedidos ?? []).filter((p) => !estaConcluido(p));
  const concluidos = (pedidos ?? []).filter(estaConcluido);

  return (
    <section>
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-foreground-500">
        Pedidos de exclusão
      </h2>

      {carregando ? (
        <div className="space-y-3">
          <div className="h-20 animate-pulse rounded-3xl bg-secondary-100" />
          <div className="h-20 animate-pulse rounded-3xl bg-secondary-100" />
        </div>
      ) : erro ? (
        <Cartao className="p-5 text-center">
          <p className="text-sm text-danger-600">{erro}</p>
          <div className="mt-3 flex justify-center">
            <Botao variante="secundario" icone="ri-refresh-line" onClick={() => void carregar()}>
              Tentar de novo
            </Botao>
          </div>
        </Cartao>
      ) : abertos.length === 0 && concluidos.length === 0 ? (
        <Cartao className="p-5">
          <p className="text-sm text-foreground-600">Nenhum pedido de exclusão.</p>
        </Cartao>
      ) : (
        <div className="space-y-3">
          {abertos.map((p) => {
            const atrasado = Number(p.dias) >= 10;
            return (
              <Cartao key={p.id} className="min-w-0 p-4 md:p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-base font-bold text-foreground-950">
                      {p.nome || 'Sem nome'}
                    </p>
                    {p.email && <p className="mt-0.5 text-sm text-foreground-600">{p.email}</p>}
                  </div>
                  <Chip tipo={atrasado ? 'warning' : 'info'}>há {p.dias} dias</Chip>
                </div>
                {p.motivo && (
                  <p className="mt-2 text-sm text-foreground-700">Motivo: {p.motivo}</p>
                )}
                <p className="mt-1 text-xs text-foreground-500">
                  Pedido em {formatarDataBR(p.pedido_em) || '—'}
                </p>
                <div className="mt-3">
                  <Botao variante="secundario" icone="ri-check-line" onClick={() => abrir(p)}>
                    Marcar como concluído
                  </Botao>
                </div>
              </Cartao>
            );
          })}

          {concluidos.length > 0 && (
            <div className="space-y-2 pt-1">
              {concluidos.map((p) => (
                <div
                  key={p.id}
                  className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 rounded-2xl border border-secondary-100 bg-background-50 px-4 py-3 opacity-60"
                >
                  <span className="min-w-0 text-sm text-foreground-700">
                    {p.nome || 'Sem nome'}
                    {p.email ? ` · ${p.email}` : ''}
                  </span>
                  <span className="shrink-0 whitespace-nowrap text-xs text-foreground-500">
                    concluído em {formatarDataBR(p.concluido_em) || '—'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <Modal
        aberto={!!alvo}
        titulo="Marcar como concluído"
        aoFechar={() => setAlvo(null)}
        rodape={
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
            <Botao variante="secundario" onClick={() => setAlvo(null)} disabled={enviando}>
              Cancelar
            </Botao>
            <Botao variante="primario" icone="ri-check-line" carregando={enviando} onClick={() => void concluir()}>
              Concluir exclusão
            </Botao>
          </div>
        }
      >
        {alvo && (
          <>
            <p className="text-sm text-foreground-700">
              Confirmar a exclusão da conta de{' '}
              <strong className="font-semibold text-foreground-950">{alvo.nome || 'Sem nome'}</strong>
              {alvo.email ? ` (${alvo.email})` : ''}?
            </p>
            <div className="mt-4">
              <CampoArea
                id="obs-exclusao"
                name="observacao"
                label="Observação (opcional)"
                value={observacao}
                onChange={setObservacao}
                maxLength={500}
                simples
              />
            </div>
            {erroModal && (
              <p className="mt-3 text-sm text-danger-600" role="alert">
                {erroModal}
              </p>
            )}
          </>
        )}
      </Modal>
    </section>
  );
}